/**
 * Module-init-safe entry point for runtime diagnostics.
 *
 * `Diagnostics/diagnostics` (the real OTLP/Loki transport) lives on a
 * cycle with `Modules/uiConfig/requestConfig` and
 * `Models/uiConfig/componentlessWidgetsConfig`:
 *
 *   componentlessWidgetsConfig
 *     -> widgetProcessor
 *     -> diagnostics
 *     -> analytics
 *     -> requestConfig
 *     -> componentlessWidgetsConfig (TDZ on `mainConfig`)
 *
 * Modules that need to log *before* `mainConfig` has been exported —
 * principally `widgetProcessor`, `templates/templates.ts`, and
 * `componentlessWidgetsConfig.ts` itself — cannot statically import
 * `Diagnostics/diagnostics` without crashing module evaluation.
 *
 * This module breaks that cycle with the runtime equivalent of "lazy
 * polymorphism": consumers import only `logRuntimeError` from here.
 * Until something registers a real implementation via
 * `setRuntimeLogger`, calls fall back to `console.error`. The first
 * thing `Diagnostics/diagnostics` does on load is register the OTLP
 * implementation, so any logs that fire after the first import of
 * the diagnostics module flow through Loki normally.
 *
 * Trade-off: log calls that fire *during* the brief
 * module-evaluation window (i.e. before `Diagnostics/diagnostics`
 * finishes loading) only land in the browser console. That window
 * is exactly when `mainConfig`/`requestConfig` are not yet
 * initialized — so OTLP could not have known the storeId or session
 * id either; `console.error` is the same fidelity we'd get from
 * `Diagnostics/diagnostics` at that point.
 */

export interface RuntimeLogPayload {
  source: string;
  error: unknown;
  // OTLP attribute values are stringly-typed; callers stringify
  // upstream so the payload that reaches the transport is already
  // serializable.
  extra?: Record<string, string>;
}

type RuntimeLogger = (payload: RuntimeLogPayload) => void;

const consoleFallback: RuntimeLogger = ({ source, error, extra }) => {
  console.error(`Convermax: ${source}`, error, extra ?? {});
};

let activeLogger: RuntimeLogger = consoleFallback;

export function setRuntimeLogger(impl: RuntimeLogger): void {
  activeLogger = impl;
}

export function logRuntimeError(payload: RuntimeLogPayload): void {
  activeLogger(payload);
}
